import { useState, useEffect, useCallback, useMemo } from 'react';
import { adminService } from '@/api/services/admin';
import type { CoachingHistoryRow, User } from '@/api/services/admin';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// ── Session grouping ──────────────────────────────────────────────────────────
// The backend stores individual turns with no session id, so we infer sessions:
// consecutive turns from the same advisor separated by less than SESSION_GAP_MS
// belong to one session.

const SESSION_GAP_MS = 30 * 60 * 1000;

interface CoachingSession {
  // Synthetic, stable across re-renders: userId + first history_id.
  key: string;
  userId: string;
  turns: CoachingHistoryRow[];
  startedAt: number;
  endedAt: number;
}

function groupIntoSessions(rows: CoachingHistoryRow[]): CoachingSession[] {
  const byUser = new Map<string, CoachingHistoryRow[]>();
  for (const row of rows) {
    const list = byUser.get(row.user_id) ?? [];
    list.push(row);
    byUser.set(row.user_id, list);
  }

  const sessions: CoachingSession[] = [];
  for (const [userId, turns] of byUser) {
    turns.sort((a, b) => Date.parse(a.created) - Date.parse(b.created));
    let current: CoachingHistoryRow[] = [];
    let prevTs = 0;
    const flush = () => {
      if (current.length === 0) return;
      sessions.push({
        key: `${userId}:${current[0].history_id}`,
        userId,
        turns: current,
        startedAt: Date.parse(current[0].created),
        endedAt: Date.parse(current[current.length - 1].created),
      });
      current = [];
    };
    for (const turn of turns) {
      const ts = Date.parse(turn.created);
      if (current.length > 0 && ts - prevTs > SESSION_GAP_MS) flush();
      current.push(turn);
      prevTs = ts;
    }
    flush();
  }

  sessions.sort((a, b) => b.startedAt - a.startedAt);
  return sessions;
}

// ── Formatting helpers ────────────────────────────────────────────────────────

function fmtDateTime(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function fmtDuration(ms: number): string {
  const mins = Math.round(ms / 60000);
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// Answers can contain raw template tokens like {ADVISORNAME}; render them as
// distinct code chips so transcripts stay readable.
function renderWithTokens(text: string) {
  const parts = text.split(/(\{[A-Z][A-Z0-9_]*\})/g);
  return parts.map((part, i) =>
    /^\{[A-Z][A-Z0-9_]*\}$/.test(part) ? (
      <code
        key={i}
        className="font-mono text-[11px] bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400 px-1 py-0.5 rounded"
      >
        {part}
      </code>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

// ── RAG chunk list ────────────────────────────────────────────────────────────

function ChunkList({ chunks }: { chunks: CoachingHistoryRow['chunks_used'] }) {
  const sorted = [...chunks].sort((a, b) => b.similarity - a.similarity);
  return (
    <div className="space-y-1 mt-2">
      {sorted.map((c, i) => (
        <div key={`${c.chunk_id}-${i}`} className="flex items-center gap-3">
          <code className="font-mono text-xs text-text-secondary w-64 flex-shrink-0 truncate" title={c.chunk_id}>
            {c.chunk_id}
          </code>
          <div className="flex-1 h-3 bg-surface rounded-sm overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-sm"
              style={{ width: `${Math.max(2, Math.round(c.similarity * 100))}%` }}
            />
          </div>
          <span className="text-xs text-text-primary tabular-nums w-12 text-right flex-shrink-0">
            {c.similarity.toFixed(3)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Single Q→A turn ───────────────────────────────────────────────────────────

function TurnView({ turn }: { turn: CoachingHistoryRow }) {
  const [showChunks, setShowChunks] = useState(false);

  return (
    <div className="space-y-2 py-3 border-b border-border last:border-0">
      <div className="flex items-start gap-3">
        <span className="text-xs text-text-muted tabular-nums w-16 flex-shrink-0 pt-0.5">
          {fmtTime(turn.created)}
        </span>
        <div className="flex-1 space-y-2 min-w-0">
          <div className="rounded-md bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-800/30 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-primary-600 dark:text-primary-400 font-medium mb-0.5">
              Advisor
            </p>
            <p className="text-sm text-text-primary whitespace-pre-wrap">{turn.user_content}</p>
          </div>
          <div className="rounded-md bg-surface border border-border px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-text-muted font-medium mb-0.5">Archie</p>
            <p className="text-sm text-text-primary whitespace-pre-wrap">
              {renderWithTokens(turn.assistant_content)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowChunks((v) => !v)}
              className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
            >
              {showChunks ? 'Hide' : 'Show'} RAG context ({turn.chunks_used.length} chunks)
            </button>
            {turn.api_log_id != null && (
              <span className="text-xs text-text-muted">
                api_log_id:{' '}
                <code className="font-mono bg-surface px-1 py-0.5 rounded">{turn.api_log_id}</code>
              </span>
            )}
          </div>
          {showChunks && <ChunkList chunks={turn.chunks_used} />}
        </div>
      </div>
    </div>
  );
}

// ── Coaching page ─────────────────────────────────────────────────────────────

export default function Coaching() {
  const [rows, setRows] = useState<CoachingHistoryRow[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError('');
    // The users join is best-effort: if it fails we still render with raw ids.
    Promise.all([
      adminService.getAllCoachingHistory(),
      adminService.getUsers().catch(() => [] as User[]),
    ])
      .then(([history, userList]) => {
        setRows(history);
        setUsers(userList);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load coaching history.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const userName = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) map.set(u.id, u.full_name || u.email);
    return (id: string) => map.get(id) ?? `${id.slice(0, 8)}…`;
  }, [users]);

  const sessions = useMemo(() => groupIntoSessions(rows), [rows]);

  // Advisors that actually have coaching activity, for the filter dropdown.
  const activeAdvisors = useMemo(() => {
    const ids = [...new Set(rows.map((r) => r.user_id))];
    return ids
      .map((id) => ({ id, name: userName(id) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, userName]);

  const filteredSessions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sessions.filter((s) => {
      if (userFilter !== 'all' && s.userId !== userFilter) return false;
      if (!q) return true;
      return s.turns.some(
        (t) =>
          t.user_content.toLowerCase().includes(q) || t.assistant_content.toLowerCase().includes(q),
      );
    });
  }, [sessions, userFilter, search]);

  // ── Stats ────────────────────────────────────────────────────────────────────

  const turnsLast7d = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return rows.filter((r) => Date.parse(r.created) >= cutoff).length;
  }, [rows]);

  // ── Loading / error ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-text-primary">Coaching</h1>
        <div className="flex items-center gap-3 p-4 rounded-md border border-error-200 dark:border-error-800/30 bg-error-50 dark:bg-error-900/20">
          <p className="text-sm text-error-700 dark:text-error-400 flex-1">{error}</p>
          <Button variant="secondary" size="sm" onClick={fetchData}>Retry</Button>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary">Coaching</h1>
          <p className="text-sm text-text-secondary mt-1">
            AskArchie coaching history — sessions are inferred by grouping an advisor&apos;s turns
            separated by less than 30 minutes.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={fetchData}>
          Refresh
        </Button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent>
            <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Total turns</p>
            <p className="text-3xl font-bold text-text-primary tabular-nums">{rows.length.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Sessions</p>
            <p className="text-3xl font-bold text-text-primary tabular-nums">{sessions.length.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Active advisors</p>
            <p className="text-3xl font-bold text-text-primary tabular-nums">{activeAdvisors.length.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Turns, last 7 days</p>
            <p className="text-3xl font-bold text-text-primary tabular-nums">{turnsLast7d.toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-text-primary mb-1">Advisor</label>
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="all">All advisors</option>
            {activeAdvisors.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-56 max-w-md">
          <Input
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search questions and answers…"
          />
        </div>
      </div>

      {/* Session list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Sessions{' '}
            <span className="text-text-muted font-normal text-sm">
              ({filteredSessions.length}{filteredSessions.length !== sessions.length ? ` of ${sessions.length}` : ''})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredSessions.length === 0 ? (
            <p className="px-4 py-6 text-sm text-text-muted text-center italic">
              No sessions match the current filters.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Advisor</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Started</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Turns</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Duration</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">First question</th>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.map((s) => (
                  <SessionRows
                    key={s.key}
                    session={s}
                    advisorName={userName(s.userId)}
                    expanded={expandedKey === s.key}
                    onToggle={() => setExpandedKey((k) => (k === s.key ? null : s.key))}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Session table row + inline transcript ─────────────────────────────────────

function SessionRows({
  session,
  advisorName,
  expanded,
  onToggle,
}: {
  session: CoachingSession;
  advisorName: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className={`border-b border-border last:border-0 cursor-pointer transition-colors ${
          expanded ? 'bg-primary-50 dark:bg-primary-500/10' : 'hover:bg-surface'
        }`}
      >
        <td className="px-4 py-2 text-sm text-text-primary font-medium">{advisorName}</td>
        <td className="px-4 py-2 text-sm text-text-secondary whitespace-nowrap tabular-nums">
          {fmtDateTime(session.startedAt)}
        </td>
        <td className="px-4 py-2">
          <Badge variant="info">{session.turns.length}</Badge>
        </td>
        <td className="px-4 py-2 text-sm text-text-secondary whitespace-nowrap">
          {session.turns.length > 1 ? fmtDuration(session.endedAt - session.startedAt) : '—'}
        </td>
        <td className="px-4 py-2 text-sm text-text-secondary max-w-md truncate" title={session.turns[0].user_content}>
          {session.turns[0].user_content}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border last:border-0">
          <td colSpan={5} className="px-4 py-2 bg-surface/50">
            <div className="max-h-[32rem] overflow-y-auto pr-2">
              {session.turns.map((t) => (
                <TurnView key={t.history_id} turn={t} />
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
